import { ReactNode, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import {
  Button,
  Input,
  Label,
  Modal,
  Select,
  showToast,
  Tag,
  Text,
} from '@mercai/clever'

import { onSuccessMessage, useValidationErrors } from '@/helpers'
import {
  SaleOrderProps,
  SupplyTransactionStatusEnum,
  UpdateSupplyOrderOnSaleOrderCancelSupplyOrders,
  UpdateSupplyOrderOnSaleOrderCancelSupplyOrdersActionsEnum,
} from '@/types'

import {
  enabledUpdateSupplyOrdersActions,
  schema,
  schemaStep1,
  schemaStep3,
} from './modal-cancellation-order.constanst'

import { ROUTES_ADMIN } from '@/router'
import {
  QUERIES_ADMIN,
  useSaleOrderSupplyOrders,
  useUpdateSupplyOrdersOnSaleOrderCancel,
} from '@/services'
import { useQueryClient } from '@tanstack/react-query'
import {
  invoiceOptions,
  paymentOptions,
  shipmentOptions,
} from '../../select-sale-order-status.constants'

import { ModalCancellationOrderProps } from './modal-cancellation-order.type'

import styles from './modal-cancellation-order.module.css'

export const ModalCancellationOrder = ({
  isOpen,
  onClose,
  saleOrder,
  onConfirm,
  isLoading,
}: ModalCancellationOrderProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('componentsSelectOrderStatus')

  const { data: supplyOrders } = useSaleOrderSupplyOrders(
    saleOrder?.uuid,
    isOpen,
  )

  const hasSupplyOrders =
    supplyOrders?.data?.supplyOrders &&
    supplyOrders?.data?.supplyOrders?.length > 0

  const { mutateAsync: updateSupplyOrdersOnSaleOrderCancel } =
    useUpdateSupplyOrdersOnSaleOrderCancel(saleOrder?.uuid)

  const [step, setStep] = useState(0)

  const [values, setValues] = useState<Partial<SaleOrderProps>>({
    ...saleOrder,
  })

  const [supplyOrdersToPayload, setSupplyOrdersToPayload] = useState<
    UpdateSupplyOrderOnSaleOrderCancelSupplyOrders[]
  >([])

  const onChangeSupplyOrder = (
    index: number,
    key: keyof UpdateSupplyOrderOnSaleOrderCancelSupplyOrders,
    value: string | undefined,
  ) => {
    setSupplyOrdersToPayload((prev) => {
      const updated = [...prev]
      updated[index] = { ...updated[index], [key]: value }
      return updated
    })
  }

  const onChangeCancellationReason = (
    key: 'reason' | 'observation',
    value: string | undefined,
  ) => {
    setValues(
      (prev) =>
        ({
          ...prev,
          cancellationReason: {
            ...prev?.cancellationReason,
            [key]: value,
          },
        }) as Partial<SaleOrderProps>,
    )
  }

  const onChangeStatus = (
    key: 'shipments' | 'payments' | 'invoices',
    index: number,
    value: string,
  ) => {
    const newArray = values?.[key]?.map((item, i) => {
      if (i === index) {
        return {
          ...item,
          status: value,
        }
      }
      return item
    })
    setValues(
      (prev) =>
        ({
          ...prev,
          [key]: newArray,
        }) as Partial<SaleOrderProps>,
    )
  }

  const valuesToPayload = useMemo(() => {
    const payload: Partial<SaleOrderProps> = {
      uuid: values?.uuid,
      status: values?.status,
      cancellationReason: values?.cancellationReason,
      shipments: values?.shipments?.map((shipment) => ({
        uuid: shipment?.uuid,
        status: shipment?.status,
      })) as SaleOrderProps['shipments'],
      payments: values?.payments?.map((payment) => ({
        uuid: payment?.uuid,
        status: payment?.status,
      })) as SaleOrderProps['payments'],
      invoices: values?.invoices?.map((invoice) => ({
        uuid: invoice?.uuid,
        status: invoice?.status,
      })) as SaleOrderProps['invoices'],
    }

    return payload
  }, [values])

  const {
    isValid: isValidStep1,
    errors: errorsStep1,
    onHandleListenErrors: onHandleListenErrorsStep1,
  } = useValidationErrors(schemaStep1(t), valuesToPayload)

  const {
    isValid: isValidStep2,
    errors: errorsStep2,
    onHandleListenErrors: onHandleListenErrorsStep2,
  } = useValidationErrors(schema(t), valuesToPayload)
  const {
    isValid: isValidStep3,
    errors: errorsStep3,
    onHandleListenErrors: onHandleListenErrorsStep3,
  } = useValidationErrors(schemaStep3(t), {
    supplyOrders: supplyOrdersToPayload,
  })

  const queryClient = useQueryClient()

  const onSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: [QUERIES_ADMIN.ORDER_CENTER.module, saleOrder?.uuid],
    })

    onClose()
  }

  const onConfirmStep1 = () => {
    if (!isValidStep1) {
      onHandleListenErrorsStep1(true)
      return
    }

    onHandleListenErrorsStep1(false)
    setStep(1)
  }

  const onConfirmStep2 = async () => {
    if (!isValidStep2) {
      onHandleListenErrorsStep2(true)
      return
    }

    onHandleListenErrorsStep2(false)

    if (hasSupplyOrders) {
      return setStep(2)
    }

    const response = await onConfirm(valuesToPayload)

    if (!response.success) {
      return showToast({
        variant: 'error',
        title: t('orderStatusesModalCancellationOrderErrorTitle'),
      })
    }

    onSuccessMessage(
      t('orderStatusesModalCancellationOrderSuccessTitle'),
      t('orderStatusesModalCancellationOrderSuccessDescription'),
    )

    onSuccess()
  }

  const onSubmit = async () => {
    if (!isValidStep2) {
      onHandleListenErrorsStep2(true)
      return
    } else {
      onHandleListenErrorsStep2(false)
    }

    if (!isValidStep3) {
      onHandleListenErrorsStep3(true)
      return
    } else {
      onHandleListenErrorsStep3(false)
    }

    const responseSaleOrderCancel = await onConfirm(valuesToPayload)

    if (!responseSaleOrderCancel.success) {
      return showToast({
        variant: 'error',
        title: t('orderStatusesModalCancellationOrderErrorTitle'),
      })
    }

    const supplyOrdersDataToPayload: UpdateSupplyOrderOnSaleOrderCancelSupplyOrders[] =
      supplyOrdersToPayload?.map((supplyOrder) => ({
        uuid: supplyOrder?.uuid || '',
        action: supplyOrder?.action,
        cancellationDescription: values?.cancellationReason?.observation || '',
      }))

    const responseUpdateSupplyOrders =
      await updateSupplyOrdersOnSaleOrderCancel({
        supplyOrders: supplyOrdersDataToPayload,
      })

    if (!responseUpdateSupplyOrders.success) {
      queryClient.invalidateQueries({
        queryKey: [QUERIES_ADMIN.ORDER_CENTER.module, saleOrder?.uuid],
      })

      onClose()

      return showToast({
        variant: 'error',
        withCloseButton: true,
        persistent: true,
        title: t('orderStatusesModalCancellationOrderErrorSupplyOrdersTitle'),
        content: t(
          'orderStatusesModalCancellationOrderErrorSupplyOrdersDescription',
        ),
      })
    }

    onSuccessMessage(
      t('orderStatusesModalCancellationOrderSuccessTitle'),
      t('orderStatusesModalCancellationOrderSuccessDescription'),
    )

    onSuccess()
  }

  const getPaymentsSelects = () => {
    if (!saleOrder || !saleOrder?.payments) return null

    return saleOrder.payments.map((payment, index) => {
      const showParcelLabel = ['partial'].includes(payment?.form)

      const parcelLabel = `(${payment?.instalment}/${saleOrder.payments?.length})`

      return (
        <div key={index}>
          <Label required>
            {t('orderStatusesModalCancellationOrderPaymentStatusLabel')} (
            {tEnums(`finance_payment_form_${payment?.form}`)}
            {showParcelLabel && ` ${parcelLabel}`})
          </Label>
          <Select
            placeholder={t('orderStatusesModalCancellationOrderPlaceholder')}
            options={paymentOptions(tEnums)}
            value={values?.payments?.[index]?.status}
            onChange={(value) =>
              onChangeStatus('payments', index, value as string)
            }
            error={
              (errorsStep2?.payments as Record<string, string>)?.[
                index
              ] as string
            }
          />
        </div>
      )
    })
  }

  const getInvoicesSelects = () => {
    if (!saleOrder || !saleOrder?.invoices) return null

    return saleOrder.invoices.map((_, index) => {
      return (
        <div key={index}>
          <Label required>
            {t('orderStatusesModalCancellationOrderInvoiceStatusLabel')}
            {saleOrder?.invoices?.length > 1 && index + 1}
          </Label>
          <Select
            placeholder={t('orderStatusesModalCancellationOrderPlaceholder')}
            options={invoiceOptions(tEnums)}
            value={values?.invoices?.[index]?.status}
            onChange={(value) =>
              onChangeStatus('invoices', index, value as string)
            }
            error={
              (errorsStep2?.invoices as Record<string, string>)?.[
                index
              ] as string
            }
          />
        </div>
      )
    })
  }

  const getShipmentsSelects = () => {
    if (!saleOrder || !saleOrder?.shipments) return null

    return saleOrder.shipments.map((_, index) => {
      return (
        <div key={index}>
          <Label required>
            {t('orderStatusesModalCancellationOrderShipmentStatusLabel')}
            {saleOrder?.shipments?.length > 1 && index + 1}
          </Label>
          <Select
            placeholder={t('orderStatusesModalCancellationOrderPlaceholder')}
            options={shipmentOptions(tEnums)}
            value={values?.shipments?.[index]?.status}
            onChange={(value) =>
              onChangeStatus('shipments', index, value as string)
            }
            error={
              (errorsStep2?.shipments as Record<string, string>)?.[
                index
              ] as string
            }
          />
        </div>
      )
    })
  }

  const statusesSupplyOrder: Record<string, ReactNode> = {
    pending: (
      <Tag variant="warning" model="light" size="small">
        {tEnums('supplier_transaction_status_pending')}
      </Tag>
    ),
    paid: (
      <Tag variant="success" model="light" size="small">
        {tEnums('supplier_transaction_status_paid')}
      </Tag>
    ),
    cancelled: (
      <Tag variant="danger" model="light" size="small">
        {tEnums('supplier_transaction_status_cancelled')}
      </Tag>
    ),
  }

  const getStepNumber = (step: number) => {
    if (hasSupplyOrders) {
      return `${step}/3`
    }

    return `${step}/2`
  }

  const steps = [
    {
      title: (
        <Text variant="text-large-bold" className={styles.title}>
          <span>{getStepNumber(1)}</span>
          <span>{t('orderStatusesModalCancellationOrderStep1Title')}</span>
        </Text>
      ),
      description: t('orderStatusesModalCancellationOrderDescription'),
      component: (
        <div className={styles.component}>
          <div>
            <Label required>
              {t('orderStatusesModalCancellationOrderCancellationReasonLabel')}
            </Label>
            <Select
              placeholder={t(
                'orderStatusesModalCancellationOrderCancellationReasonPlaceholder',
              )}
              options={[1, 2, 3, 4, 5, 6].map((number) => ({
                label: t(
                  `orderStatusesModalCancellationOrderCancellationReasonOption${number}`,
                ),
                value: t(
                  `orderStatusesModalCancellationOrderCancellationReasonOption${number}`,
                ),
              }))}
              value={values?.cancellationReason?.reason}
              onChange={(value) => onChangeCancellationReason('reason', value)}
              error={
                (errorsStep1?.cancellationReason as string) ||
                ((errorsStep1?.cancellationReason as Record<string, string>)
                  ?.reason as string)
              }
            />
          </div>

          <div>
            <Label>
              {t(
                'orderStatusesModalCancellationOrderCancellationObservationLabel',
              )}
            </Label>
            <Input
              placeholder={t(
                'orderStatusesModalCancellationOrderCancellationObservationPlaceholder',
              )}
              isTextArea={true}
              value={values?.cancellationReason?.observation}
              onChange={(value) =>
                onChangeCancellationReason('observation', value)
              }
              error={
                (errorsStep1?.cancellationReason as Record<string, string>)
                  ?.observation as string
              }
            />
          </div>

          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              {t('orderStatusesModalCancellationOrderCancelButton')}
            </Button>

            <Button variant="primary" onClick={onConfirmStep1}>
              {t('orderStatusesModalCancellationOrderConfirmButton')}
            </Button>
          </div>
        </div>
      ),
    },
    {
      title: (
        <Text variant="text-large-bold" className={styles.title}>
          <span>{getStepNumber(2)}</span>
          <span>{t('orderStatusesModalCancellationOrderStep2Title')}</span>
        </Text>
      ),
      description: t('orderStatusesModalCancellationOrderDescription'),
      component: (
        <div className={styles.component}>
          {getPaymentsSelects()}
          {getInvoicesSelects()}
          {getShipmentsSelects()}

          <div className={styles.footer}>
            <Button
              variant="secondary"
              onClick={() => setStep(0)}
              disabled={isLoading}
              isActive={!isLoading}
            >
              {t('orderStatusesModalCancellationOrderPrevButton')}
            </Button>

            <Button
              variant="primary"
              onClick={onConfirmStep2}
              disabled={isLoading}
              isActive={!isLoading}
            >
              {t('orderStatusesModalCancellationOrderConfirmButton')}
            </Button>
          </div>
        </div>
      ),
    },
    {
      title: (
        <Text variant="text-large-bold" className={styles.title}>
          <span>{getStepNumber(3)}</span>
          <span>{t('orderStatusesModalCancellationOrderStep3Title')}</span>
        </Text>
      ),
      description: t('orderStatusesModalCancellationOrderDescription'),
      component: (
        <div className={styles.component}>
          {supplyOrdersToPayload?.map((supplyOrder, i) => (
            <div key={supplyOrder?.uuid} className={styles['supply-order']}>
              <div className={styles['supply-order-payment-label']}>
                <Text variant="title-base">
                  {t(
                    'orderStatusesModalCancellationSupplyOrderSupplyTransactionStatusLabel',
                  )}
                  :
                </Text>
                {
                  statusesSupplyOrder?.[
                    supplyOrders?.data?.supplyOrders?.[i]?.payments?.[0]
                      ?.statements?.[0]?.transaction?.status || 'pending'
                  ]
                }
              </div>
              <Text
                variant="text-medium"
                className={styles['supply-order-label']}
              >
                <strong>
                  <Link
                    to={`${ROUTES_ADMIN.SUPPLY.ORDER_MANAGEMENT.PURCHASE_ORDER.fullPath()}?search=${supplyOrders?.data?.supplyOrders?.[i]?.reference}&showUuid=${supplyOrders?.data?.supplyOrders?.[i]?.uuid}`}
                    target="_blank"
                    className={styles.link}
                  >
                    {`${supplyOrders?.data?.supplyOrders?.[i]?.reference} | ${supplyOrders?.data?.supplyOrders?.[i]?.supplyQuotation?.purchaseOrderCode}`}
                  </Link>
                </strong>
              </Text>
              <Label required>
                {t('orderStatusesModalCancellationSupplyOrderActionsLabel')}
              </Label>
              <Select
                value={supplyOrder?.action}
                options={
                  typeof supplyOrders?.data?.supplyOrders?.[i]?.payments?.[0]
                    ?.statements?.[0]?.transaction?.status === 'string' &&
                  enabledUpdateSupplyOrdersActions?.[
                    supplyOrders?.data?.supplyOrders?.[i]?.payments?.[0]
                      ?.statements?.[0]?.transaction
                      ?.status as keyof typeof SupplyTransactionStatusEnum
                  ]
                    ? enabledUpdateSupplyOrdersActions?.[
                        supplyOrders?.data?.supplyOrders?.[i]?.payments?.[0]
                          ?.statements?.[0]?.transaction
                          ?.status as keyof typeof SupplyTransactionStatusEnum
                      ]?.map((key) => ({
                        value:
                          key as keyof typeof UpdateSupplyOrderOnSaleOrderCancelSupplyOrdersActionsEnum,
                        label: tEnums(
                          `update_supply_order_on_sale_order_cancel_supply_orders_actions_${key}`,
                        ),
                      }))
                    : [
                        {
                          value: 'cancel_all',
                          label: tEnums(
                            'update_supply_order_on_sale_order_cancel_supply_orders_actions_cancel_all',
                          ),
                        },
                      ]
                }
                onChange={(value) => onChangeSupplyOrder(i, 'action', value)}
                error={
                  errorsStep3[`supplyOrders[${i}]`]
                    ? t('requiredMessage')
                    : undefined
                }
              />
            </div>
          ))}

          <div className={styles.footer}>
            <Button
              variant="secondary"
              onClick={() => setStep(1)}
              disabled={isLoading}
              isActive={!isLoading}
            >
              {t('orderStatusesModalCancellationOrderPrevButton')}
            </Button>

            <Button
              variant="primary"
              onClick={onSubmit}
              disabled={isLoading}
              isActive={!isLoading}
            >
              {t('orderStatusesModalCancellationOrderConfirmButton')}
            </Button>
          </div>
        </div>
      ),
    },
  ]

  useEffect(() => {
    setValues({
      ...saleOrder,
    })
  }, [saleOrder, isOpen])

  useEffect(() => {
    setStep(0)

    if (!isOpen) {
      setSupplyOrdersToPayload([])
    }
  }, [isOpen])

  useEffect(() => {
    setSupplyOrdersToPayload(
      (supplyOrders?.data?.supplyOrders?.map((order) => ({
        uuid: order?.uuid,
      })) as UpdateSupplyOrderOnSaleOrderCancelSupplyOrders[]) || [],
    )

    return () => {
      setSupplyOrdersToPayload([])
    }
  }, [supplyOrders?.data])

  return (
    <Modal.Root
      isOpen={isOpen}
      onHandleOpen={onClose}
      size="small"
      mainClassName={styles.modal}
    >
      <Modal.Header
        title={steps?.[step].title || ''}
        description={steps?.[step].description || ''}
        className={styles.header}
      />
      <Modal.Body className={styles.body}>{steps?.[step].component}</Modal.Body>
    </Modal.Root>
  )
}
